import { useEffect, useState } from 'react';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../App';
import { Medication } from '../types';
import { Package, Plus, Search, AlertTriangle, Trash2, Edit2, Save, X, Calendar, DollarSign, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function Inventory() {
  const { user } = useAuth();
  const [medications, setMedications] = useState<Medication[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState<Partial<Medication>>({
    name: '',
    activeIngredient: '',
    pharmacologicalClass: '',
    presentations: '',
    price: 0,
    stock: 0,
    batch: '',
    expirationDate: '',
    minStock: 5
  });

  useEffect(() => {
    const q = query(collection(db, 'medications'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Medication));
      setMedications(docs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateDoc(doc(db, 'medications', editingId), formData);
        setEditingId(null);
      } else {
        await addDoc(collection(db, 'medications'), {
          ...formData,
          createdAt: new Date().toISOString()
        });
        setIsAdding(false);
      }
      setFormData({
        name: '',
        activeIngredient: '',
        pharmacologicalClass: '',
        presentations: '',
        price: 0,
        stock: 0,
        batch: '',
        expirationDate: '',
        minStock: 5
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este medicamento?')) {
      await deleteDoc(doc(db, 'medications', id));
    }
  };

  const handleUpdateField = async (id: string, field: string, value: string) => {
    try {
      await updateDoc(doc(db, 'medications', id), { [field]: value });
    } catch (err) {
      console.error('Erro ao atualizar campo:', err);
    }
  };

  const startEdit = (med: Medication) => {
    setEditingId(med.id);
    setFormData(med);
    setIsAdding(true);
  };

  const handleBulkPriceUpdate = async () => {
    if (window.confirm('Deseja aplicar um reajuste de 10% em todos os medicamentos?')) {
      for (const med of medications) {
        await updateDoc(doc(db, 'medications', med.id), {
          price: med.price * 1.1
        });
      }
    }
  };

  const filteredMedications = medications.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.activeIngredient.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // FEFO Logic: Sort by expiration date for display
  const sortedMedications = [...filteredMedications].sort((a, b) => 
    new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime()
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-main flex items-center">
            <Package className="w-6 h-6 mr-2 text-primary" /> Controle de Estoque
          </h1>
          <p className="text-text-muted">Gerencie medicamentos, lotes e vencimentos.</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={handleBulkPriceUpdate}
            className="inline-flex items-center px-4 py-2 bg-secondary text-white font-bold rounded-lg hover:bg-secondary-dark transition-colors shadow-md"
          >
            <DollarSign className="w-5 h-5 mr-2" /> Atualizar Preços (10%)
          </button>
          <button
            onClick={() => { setIsAdding(true); setEditingId(null); }}
            className="inline-flex items-center px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition-colors shadow-md"
          >
            <Plus className="w-5 h-5 mr-2" /> Novo Medicamento
          </button>
        </div>
      </header>

      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-text-muted" />
        </div>
        <input
          type="text"
          placeholder="Buscar por nome ou princípio ativo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary sm:text-sm"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-text-muted/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-text-muted/10">
            <thead className="bg-bg-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">Medicamento</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">Estoque</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">Lote / Vencimento</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-text-muted uppercase tracking-wider">Preço</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-text-muted uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-text-muted/10">
              {sortedMedications.map((med) => {
                const isLowStock = med.stock <= med.minStock;
                const isNearExpiration = new Date(med.expirationDate).getTime() - new Date().getTime() < 30 * 24 * 60 * 60 * 1000; // 30 days
                
                return (
                  <tr key={med.id} className="hover:bg-bg-light transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-bold text-text-main">{med.name}</div>
                          <div className="text-xs text-text-muted">{med.activeIngredient}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-bold ${isLowStock ? 'text-secondary-dark' : 'text-text-main'}`}>
                          {med.stock} unidades
                        </span>
                        {isLowStock && <AlertTriangle className="w-4 h-4 text-secondary ml-2" />}
                      </div>
                      <div className="text-xs text-text-muted">Mínimo: {med.minStock}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-main flex items-center">
                        <Tag className="w-3 h-3 mr-1 text-text-muted" />
                        <span>Lote: {med.batch}</span>
                      </div>
                      <div className={`flex items-center mt-1 text-xs font-medium ${isNearExpiration ? 'text-secondary-dark font-bold' : 'text-text-muted'}`}>
                        <Calendar className="w-3 h-3 mr-1" />
                        <input
                          type="text"
                          defaultValue={med.expirationDate}
                          onBlur={(e) => handleUpdateField(med.id, 'expirationDate', e.target.value)}
                          placeholder="AAAA-MM-DD"
                          className="bg-transparent border-none p-0 focus:ring-0 w-24 text-xs font-medium hover:bg-bg-light rounded transition-colors"
                          title="Clique para editar a data (AAAA-MM-DD)"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-main">
                      R$ {med.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => startEdit(med)} className="text-primary hover:text-primary-dark mr-4">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(med.id)} className="text-red-600 hover:text-red-900">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isAdding && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-text-main/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
            >
              <div className="px-8 py-6 border-b border-text-muted/10 flex justify-between items-center bg-bg-light">
                <h2 className="text-xl font-bold text-text-main">
                  {editingId ? 'Editar Medicamento' : 'Novo Medicamento'}
                </h2>
                <button onClick={() => setIsAdding(false)} className="text-text-muted hover:text-text-main">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Nome Comercial</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Princípio Ativo</label>
                  <input
                    type="text"
                    value={formData.activeIngredient}
                    onChange={(e) => setFormData({ ...formData, activeIngredient: e.target.value })}
                    className="w-full border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Classe Farmacológica</label>
                  <input
                    type="text"
                    value={formData.pharmacologicalClass}
                    onChange={(e) => setFormData({ ...formData, pharmacologicalClass: e.target.value })}
                    className="w-full border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Apresentação</label>
                  <input
                    type="text"
                    value={formData.presentations}
                    onChange={(e) => setFormData({ ...formData, presentations: e.target.value })}
                    className="w-full border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Ex: 500mg - 30 comprimidos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Preço (R$)</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-4 w-4 text-text-muted" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      className="w-full pl-10 border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Estoque Atual</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) })}
                    className="w-full border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Lote</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Tag className="h-4 w-4 text-text-muted" />
                    </div>
                    <input
                      type="text"
                      value={formData.batch}
                      onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                      className="w-full pl-10 border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Data de Vencimento (AAAA-MM-DD)</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-4 w-4 text-text-muted" />
                    </div>
                    <input
                      type="text"
                      placeholder="Ex: 2026-12-31"
                      value={formData.expirationDate}
                      onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                      className="w-full pl-10 border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-main/70 mb-1">Estoque Mínimo (Alerta)</label>
                  <input
                    type="number"
                    value={formData.minStock}
                    onChange={(e) => setFormData({ ...formData, minStock: parseInt(e.target.value) })}
                    className="w-full border-text-muted/30 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              <div className="p-8 bg-bg-light border-t border-text-muted/10 flex justify-end space-x-4">
                <button
                  onClick={() => setIsAdding(false)}
                  className="px-6 py-2 text-sm font-medium text-text-muted hover:text-text-main"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="px-8 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark shadow-md transition-all"
                >
                  <Save className="w-4 h-4 inline mr-2" /> Salvar Medicamento
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
